import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "@phosphor-icons/react";
import { SITE } from "@/data/content";
import { useSmoothScroll } from "./useSmoothScroll";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.4 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const scrollTo = useSmoothScroll();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden grain">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src="/assets/hero-bg.png"
          alt="Cinematic editing studio bathed in warm golden light"
          className="w-full h-full object-cover"
          data-testid="hero-background-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-void via-void/70 to-void/40" />
      <div className="absolute inset-0 glow-gold opacity-60 blur-[100px]" />

      {/* Floating editing-studio panels — pure CSS/DOM, lightweight */}
      <div className="absolute inset-0 pointer-events-none hidden lg:block" aria-hidden="true">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: [0, -14, 0] }}
          transition={{ opacity: { duration: 1, delay: 1.6 }, y: { duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1.6 } }}
          className="absolute top-[22%] right-[6%] w-72 rounded-2xl glass border border-gold/25 p-4 shadow-[0_0_60px_rgba(229,169,60,0.15)]"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Timeline · 4K</span>
            <span className="flex items-center gap-1.5 font-mono text-[10px] text-zinc-400">
              <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" /> REC
            </span>
          </div>
          <div className="space-y-2">
            <div className="flex gap-1.5 h-4">
              <div className="w-1/4 rounded-sm bg-gold/70" />
              <div className="w-1/6 rounded-sm bg-gold/30" />
              <div className="w-1/3 rounded-sm bg-gold/50" />
              <div className="flex-1 rounded-sm bg-white/10" />
            </div>
            <div className="flex gap-1.5 h-4">
              <div className="w-1/6 rounded-sm bg-white/10" />
              <div className="w-1/2 rounded-sm bg-gold/40" />
              <div className="flex-1 rounded-sm bg-gold/20" />
            </div>
            <div className="relative h-px bg-white/15">
              <motion.div
                animate={{ left: ["0%", "100%"] }}
                transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                className="absolute -top-1.5 h-3 w-px bg-gold shadow-[0_0_8px_rgba(229,169,60,0.9)]"
              />
            </div>
          </div>
          <p className="mt-3 font-mono text-[10px] text-zinc-500 tracking-widest">24 FPS · GRADE: GOLD NOIR</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: [0, 12, 0] }}
          transition={{ opacity: { duration: 1, delay: 1.9 }, y: { duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1.9 } }}
          className="absolute bottom-[24%] right-[16%] w-48 rounded-2xl glass border border-white/10 p-4"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Color Grade</span>
          <div className="mt-3 flex items-center gap-3">
            <div className="h-12 w-12 rounded-full border border-gold/40" style={{ background: "conic-gradient(from 180deg, #E5A93C, #7a5313, #2a1d08, #E5A93C)" }} />
            <div className="space-y-1.5">
              <div className="h-1.5 w-16 rounded-full bg-gold/60" />
              <div className="h-1.5 w-12 rounded-full bg-white/15" />
              <div className="h-1.5 w-20 rounded-full bg-gold/30" />
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-[88rem] mx-auto w-full px-6 md:px-10 pb-28 md:pb-40 pt-44">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mb-8 inline-flex items-center gap-2.5 rounded-full glass border border-white/10 px-4 py-2"
          data-testid="availability-badge"
        >
          <span className="h-2 w-2 rounded-full bg-gold animate-pulse-dot" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-300">
            Available for Creative Projects
          </span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="font-mono text-xs uppercase tracking-[0.35em] mb-6"
          data-testid="hero-brand"
        >
          <span className="text-zinc-100 font-semibold">MARKMAXO</span>
          <span className="text-gold"> — BY INDER</span>
        </motion.p>

        <h1 className="font-display font-black tracking-tight leading-[1.05] text-[clamp(2.5rem,8.5vw,7.5rem)] text-zinc-50" data-testid="hero-title">
          {SITE.heroTitle.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "italic text-gold" : ""}`}
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-8 max-w-xl text-base md:text-lg text-zinc-400 leading-relaxed"
          data-testid="hero-subtitle"
        >
          {SITE.heroSub}
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-4 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500"
          data-testid="hero-tagline"
        >
          {SITE.tagline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.25 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-explore-work-button"
            onClick={() => scrollTo("#work")}
            className="group inline-flex items-center gap-2 rounded-full bg-gold px-9 py-4 text-[15px] font-semibold uppercase tracking-wide text-void hover:bg-gold-soft transition-colors duration-300"
          >
            View My Work
            <ArrowDown size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-y-0.5" />
          </button>
          <button
            data-testid="hero-contact-button"
            onClick={() => scrollTo("#contact", '[data-testid="contact-name-input"]')}
            className="group inline-flex items-center gap-2 rounded-full border border-white/20 glass px-9 py-4 text-[15px] font-semibold uppercase tracking-wide text-zinc-100 hover:border-gold hover:text-gold transition-colors duration-300"
          >
            Let&apos;s Create
            <ArrowUpRight size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}
